import { useCallback, useEffect, useRef, useState } from 'react';

import { selectCallMinimalMode } from '@app/model/app-settings';
import { useAppSelector } from '@app/store/hooks';
import { selectAuthUser } from '@entities/auth/model/selectors';
import { CallState, Direction } from '@entities/call-item';
import { useNotifications } from '@entities/notifications';

import { CONFERENCE_FACTORY_URI, REGISTRAR_SERVER, WS_SERVER } from '../config';
import { JsSIPAdapter } from '../lib/jssip-adapter';
import { getMediaStreamConstraints } from '../lib/media';
import { validateSipLogin } from '../lib/misc';
import { registerSipLogoutHandler } from '../lib/sip-logout-coordinator';
import {
	ConferenceInfo,
	type HistoricalCall,
	type JsSipConfig,
	ParkedDialog,
	ParkFailureEvent,
	ParkProgressEvent,
	SessionInfo,
	SignalQuality,
	SipTarget,
	WatchSubscriberOptions,
} from '../model/types';
import { useCallsList } from './use-calls-list';
import { useDeviceSettings } from './use-device-settings';
import { useWindowUnload } from './use-window-unload';

export function useSipClient(autoStart = true) {
	const authUser = useAppSelector(selectAuthUser);
	const callMinimalMode = useAppSelector(selectCallMinimalMode);
	const [isRegistered, setIsRegistered] = useState(false);
	const [signalQuality, setSignalQuality] = useState<SignalQuality>('good');
	const [isLoadingRequest, setIsLoadingRequest] = useState(false);
	const [localStream, setLocalStream] = useState<MediaStream | null>(null);
	const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

	// флаг, что редирект ведёт на focus, чтобы пометить звонок конференцией ещё до подписки.
	const isConferenceRef = useRef(false);
	// кого пригласить в focus после установления
	const conferenceInviteeRef = useRef<SipTarget | null>(null);
	// исходный вызов, который переводим в конференцию (REFER with Replaces).
	const heldSessionIdRef = useRef<HistoricalCall | null>(null);

	const screenStreamRef = useRef<MediaStream | null>(null);
	const adapterRef = useRef<JsSIPAdapter | null>(null);

	const {
		currentCall,
		patchCall,
		setCurrentCall,
		setCallState,
		updateMedia,
		setScreenShare,
		addCall,
		removeCall,
		addParkingCall,
		removeParkingCall,
		clearParkingCalls,
		clearCalls,
	} = useCallsList();
	const { selectedAudioInput, selectedVideoInput } = useDeviceSettings();

	const { pushNotification } = useNotifications();

	const stopScreenStream = useCallback(() => {
		screenStreamRef.current?.getTracks().forEach((t) => t.stop());
		screenStreamRef.current = null;
	}, []);

	const disableScreenShare = useCallback(
		(sessionId: string) => {
			setScreenShare({ id: sessionId, active: false });
			stopScreenStream();
		},
		[setScreenShare, stopScreenStream]
	);

	const handleError = (error: unknown, duration = 3000) => {
		if (!error) return;

		const message =
			error instanceof Error
				? error.message
				: Array.isArray(error)
					? error.join(' ')
					: typeof error === 'string'
						? error
						: 'Неизвестная ошибка';

		pushNotification({
			message,
			duration,
			type: 'error',
			hasRemove: true,
		});
	};

	const buildConstraints = useCallback(
		(withVideo = false) =>
			getMediaStreamConstraints(withVideo, selectedAudioInput, selectedVideoInput),
		[selectedAudioInput, selectedVideoInput]
	);

	const register = useCallback(() => {
		try {
			const adapter = adapterRef.current;
			// Если уже зарегистрированы — ничего не делаем
			if (!adapter || adapter.isRegistered()) return;

			// Если WebSocket уже поднят — просто регаемся, иначе стартуем и регаемся
			if (adapter.isConnected()) {
				adapter.register();
			} else {
				adapter.start();
			}
		} catch (error) {
			handleError(error);
		}
	}, []);

	const unregister = useCallback(() => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			// Разрегистрируемся даже в офлайне, чтобы отключить автоматические retry-регистрации.
			adapter.unregister();
		} catch (error) {
			handleError(error);
		}
	}, []);

	const call = useCallback(
		(target: SipTarget, mediaConstraintsOverride?: MediaStreamConstraints) => {
			try {
				const adapter = adapterRef.current;
				if (!adapter) return;

				const { aor, displayName } = target;
				const session = adapter.call(aor, mediaConstraintsOverride);

				patchCall({
					id: session.id,
					changes: {
						local: {
							audio: true,
							video: Boolean(mediaConstraintsOverride?.video),
						},
						displayName,
					},
				});
				setCurrentCall(session.id);

				return session;
			} catch (error) {
				handleError(error);
				return null;
			}
		},
		[]
	);

	const answer = useCallback((sesId: string, mediaConstraintsOverride?: MediaStreamConstraints) => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			const session = adapter.getSessionById(sesId);
			if (!session) return;

			adapter.answer(sesId, mediaConstraintsOverride);
			patchCall({
				id: session.id,
				changes: {
					local: {
						audio: true,
						video: Boolean(mediaConstraintsOverride?.video),
					},
				},
			});
			setCurrentCall(sesId);
		} catch (error) {
			handleError(error);
		}
	}, []);

	const reject = useCallback((sesId: string) => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			const session = adapter.getSessionById(sesId);
			if (!session) return;

			adapter.hangup(sesId);
			patchCall({
				id: sesId,
				changes: { state: 'declined' },
			});
		} catch (error) {
			handleError(error);
		}
	}, []);

	const hangup = useCallback((sesId?: string) => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			const session = sesId ? adapter.getSessionById(sesId) : adapter.getCurrentSession();
			if (!session) return;

			adapter.hangup(session.id);
		} catch (error) {
			handleError(error);
		}
	}, []);

	const getCallIdBySessionId = useCallback((sessionId: string): string | null => {
		try {
			const adapter = adapterRef.current;
			if (!adapter || !sessionId) return null;

			return adapter.getSessionCallId(sessionId);
		} catch {
			return null;
		}
	}, []);

	const hold = useCallback(async () => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			const session = adapter.getCurrentSession();
			if (!session) return;

			await adapter.hold(session.id);
		} catch {
			handleError('Ошибка при переключении удержания');
		}
	}, []);

	const resumeById = useCallback(async (id: string) => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			const session = adapter.getSessionById(id);
			if (!session) return;

			await adapter.unhold(session.id);
		} catch {
			handleError('Ошибка при возобновлении вызова');
		}
	}, []);

	const setAudioEnabled = useCallback(async (enabled: boolean) => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			const session = adapter.getCurrentSession();
			if (!session) return;

			updateMedia({ id: session.id, target: 'local', changes: { audio: enabled } });

			await (enabled ? adapter.unmuteAudio(session.id) : adapter.muteAudio(session.id));
		} catch (error) {
			handleError(error);
		}
	}, []);

	const setVideoEnabled = useCallback(
		async (enabled: boolean) => {
			try {
				const adapter = adapterRef.current;
				if (!adapter) return;

				const session = adapter.getCurrentSession();
				if (!session) return;

				updateMedia({ id: session.id, target: 'local', changes: { video: enabled } });

				if (enabled) {
					// 1. гарантированно берём свежую камеру
					const camStream = await navigator.mediaDevices.getUserMedia({
						video: { deviceId: { exact: selectedVideoInput } },
					});

					const newTrack = camStream.getVideoTracks()[0];

					await adapter.unmuteVideo(session.id, newTrack);
				} else await adapter.muteVideo(session.id);
			} catch (error) {
				handleError(error);
			}
		},
		[selectedVideoInput]
	);

	const shareScreen = useCallback(
		async (enabled: boolean) => {
			const adapter = adapterRef.current;
			if (!adapter) return;

			const session = adapter.getCurrentSession();
			if (!session) return;

			try {
				const stopSharing = async () => {
					disableScreenShare(session.id);
					await setVideoEnabled(false);
				};

				if (!enabled) {
					await stopSharing();
					return;
				}

				setScreenShare({ id: session.id, active: true });
				const screenStream = await navigator.mediaDevices.getDisplayMedia({
					video: {
						frameRate: { ideal: 30, max: 60 },
						width: { ideal: 1920, max: 3840 },
						height: { ideal: 1080, max: 2160 },
					},
					audio: false, // таб-аудио, но...
				});

				await adapter.shareScreen(session.id, screenStream);

				screenStream.getVideoTracks()[0].addEventListener('ended', stopSharing);
				screenStreamRef.current = screenStream;
			} catch (error) {
				setScreenShare({ id: session.id, active: false });
				handleError(`Ошибка при шаринге экрана: ${String(error)}`);
			}
		},
		[disableScreenShare, setVideoEnabled]
	);

	const transferCall = useCallback((target: SipTarget) => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			const session = adapter.getCurrentSession();
			if (!session) return;

			adapter.transferCall(session.id, target);
		} catch (error) {
			handleError(error);
		}
	}, []);

	const parkingCall = useCallback(() => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			const session = adapter.getCurrentSession();
			if (!session) return;

			adapter.parkCall(session.id, currentCall?.displayName);
		} catch (error) {
			handleError(error);
		}
	}, [currentCall]);

	const createConference = useCallback(
		async (target: SipTarget) => {
			try {
				const adapter = adapterRef.current;
				if (!adapter) return;

				const session = adapter.getCurrentSession();
				if (!session) return;

				const { isConference } = session;
				if (isConference) {
					adapter.referTarget(session.aor, target, session.id);
				} else {
					// Ставим на холд текущий вызов
					await adapter.hold(session.id);

					const newSession = adapter.call(CONFERENCE_FACTORY_URI);

					if (newSession?.id) {
						heldSessionIdRef.current = currentCall;
						conferenceInviteeRef.current = target;
						setCurrentCall(newSession?.id);
					}
				}
			} catch (error) {
				handleError(error);
			}
		},
		[currentCall]
	);

	const resumeParkedDialog = useCallback(
		(dialog: ParkedDialog, mediaConstraintsOverride?: MediaStreamConstraints) => {
			try {
				const adapter = adapterRef.current;
				if (!adapter) return;

				const session = adapter.resumeParkedDialog(dialog, mediaConstraintsOverride);
				if (!session) return;

				patchCall({
					id: session.id,
					changes: {
						local: {
							audio: true,
							video: Boolean(mediaConstraintsOverride?.video),
						},
						displayName: dialog.remoteDisplayName,
					},
				});

				setCurrentCall(session.id);
			} catch (error) {
				handleError(error);
			} finally {
				removeParkingCall(dialog.id);
			}
		},
		[]
	);

	const watchConference = useCallback((target: string, options?: WatchSubscriberOptions) => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return null;

			return adapter.watchConference(target, options);
		} catch (error) {
			handleError(error);
			return null;
		}
	}, []);

	const unwatchConference = useCallback(() => {
		try {
			const adapter = adapterRef.current;
			if (!adapter) return;

			if (!currentCall?.isConference) return;

			const session = adapter.getSessionById(currentCall.id);
			if (!session) return;

			const focusUri = session.aor || session.session?.remote_identity?.uri?.toString();
			adapter.unwatchConference(focusUri);
		} catch (error) {
			handleError(error);
		}
	}, [currentCall]);

	const switchAudioInput = async (deviceId?: string) => {
		try {
			if (!deviceId) return;

			const adapter = adapterRef.current;
			if (!adapter) return;

			const sessions = adapter.getAllSessions();

			// Получаем новый аудио-трек из конкретного устройства
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: { deviceId: { exact: deviceId } },
				video: false,
			});
			const newTrack = stream.getAudioTracks()[0];

			sessions.forEach((ses) => {
				if (ses) adapter.switchMediaInput(ses.id, newTrack);
			});
		} catch (error) {
			handleError(error);
		}
	};

	const switchVideoInput = async (deviceId?: string) => {
		try {
			if (!deviceId) return;

			const adapter = adapterRef.current;
			if (!adapter) return;

			const sessions = adapter.getAllSessions();

			// 1) Захватываем новую камеру
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { deviceId: { exact: deviceId } },
				audio: false,
			});

			const newTrack = stream.getVideoTracks()[0];

			sessions.forEach((ses) => {
				if (ses) adapter.switchMediaInput(ses.id, newTrack);
			});
		} catch (error) {
			handleError(error);
		}
	};

	const getSelfUri = useCallback(() => {
		const adapter = adapterRef.current;
		if (!adapter) return;

		return adapter.getSelfUri();
	}, []);

	const shutdownSipSession = useCallback(() => {
		const adapter = adapterRef.current;
		if (!adapter) return;

		try {
			adapter.getAllSessions().forEach((session) => {
				if (session?.id) {
					adapter.hangup(session.id);
				}
			});

			const currentSipUri = adapter.getSelfUri();
			if (validateSipLogin(currentSipUri, [])) {
				adapter.unwatchRegister(currentSipUri);
				adapter.unwatchCallParking(currentSipUri);
			}

			adapter.getAllSessions().forEach((session) => {
				if (!session?.isConference) return;
				const focusUri = session.aor || session.session?.remote_identity?.uri?.toString?.();
				if (focusUri) {
					adapter.unwatchConference(focusUri);
				}
			});
			adapter.unregister();
		} catch (error) {
			console.warn('[SIP] Failed to shutdown SIP session', error);
		} finally {
			stopScreenStream();
			clearParkingCalls();
			clearCalls();
			setCurrentCall(null);
			setLocalStream(null);
			setRemoteStream(null);
			setIsRegistered(false);
			setIsLoadingRequest(false);
			setSignalQuality('good');

			adapter.removeAllListeners();
			adapter.stop();

			if (adapterRef.current === adapter) {
				adapterRef.current = null;
			}
		}
	}, [clearCalls, clearParkingCalls, setCurrentCall, stopScreenStream]);

	useEffect(() => {
		return registerSipLogoutHandler(shutdownSipSession);
	}, [shutdownSipSession]);

	useWindowUnload(shutdownSipSession);

	useEffect(() => {
		if (!autoStart) return;

		const resolvedSipUri = authUser?.sipAor;
		if (!resolvedSipUri || !validateSipLogin(resolvedSipUri, [])) {
			// если логин некорректный — гарантированно гасим старый адаптер
			const adapter = adapterRef.current;
			if (adapter) {
				adapter.stop();
				adapterRef.current = null;
			}
			return;
		}

		if (adapterRef.current) return;

		const adapterConfig: JsSipConfig = {
			uri: resolvedSipUri,
			websocketUrl: WS_SERVER,
			// sessionTimers: true,
			// sessionTimersRefreshMethod: 'INVITE',
			registrarServer: REGISTRAR_SERVER,
			autoReconnect: true,
		};
		if (callMinimalMode) {
			adapterConfig.disableCallParkingSubscription = true;
		}
		if (authUser?.name) {
			adapterConfig.displayName = authUser.name;
		}

		const adapter = new JsSIPAdapter(adapterConfig);
		adapterRef.current = adapter;
		adapter.start();
	}, [autoStart, authUser?.name, authUser?.sipAor, callMinimalMode]);

	useEffect(() => {
		const adapter = adapterRef.current;
		if (!adapter) return;

		// Registration events
		const onRegistered = () => setIsRegistered(true);
		const onUnregistered = () => setIsRegistered(false);
		const onRegistrationFailed = (e: any) => {
			setIsRegistered(false);

			const message = e?.response?.reason_phrase || e?.cause || e?.message;
			const code = Number(e?.response?.status_code || 0);
			handleError(`Ошибка регистрации ${code}, ${message}`);
		};

		// Новый диалог
		const onSessionCreated = (info: SessionInfo) => {
			const { session, aor, displayName } = info;

			const direction = String(session.direction) as Direction;

			const baseCall: HistoricalCall = {
				id: session.id,
				aor,
				displayName,
				direction,
				state: direction === 'incoming' ? 'incoming' : 'calling',
				startedAt: null,
				endedAt: null,
				screenShare: false,
				isConference: info.isConference || isConferenceRef.current,
				local: { audio: true, video: false },
				remote: { audio: true, video: false },
			};

			const isReplacing = Boolean(info.isReplaces && currentCall);
			const sameCaller = isReplacing && aor === currentCall?.aor;
			const shouldActivateExisting = isReplacing && (info.isParked || sameCaller);
			const shouldAddActiveCall = isReplacing && info.isConference && !shouldActivateExisting;

			if (!isReplacing || !currentCall) {
				addCall(baseCall);
			} else if (shouldActivateExisting) {
				patchCall({
					id: currentCall.id,
					changes: {
						...baseCall,
						state: 'active',
						aor: currentCall.aor,
						displayName: currentCall.displayName,
					},
				});
				setCurrentCall(session.id);
			} else {
				addCall(shouldAddActiveCall ? { ...baseCall, state: 'active' } : baseCall);
				setCurrentCall(session.id);
			}

			if (isConferenceRef.current && currentCall) {
				info.isConference = true;
				queueMicrotask(() => setCurrentCall(session.id));
				removeCall(currentCall.id);
				isConferenceRef.current = false;
			}
		};

		// Call lifecycle
		const onAccepted = ({ session, aor, isReplaces, isParked, isConference }: SessionInfo) => {
			patchCall({ id: session.id, changes: { startedAt: session.start_time.toISOString() } });

			if (isReplaces) {
				setCallState({ id: session.id, state: isParked ? 'parked' : 'active' });
			} else {
				setCallState({ id: session.id, state: 'active' });
			}

			if (isConference) {
				const focusUri = aor;
				setCurrentCall(session.id);
				watchConference(focusUri, { expires: 3600 });

				// После успешного установления вызова на focus — отправляем REFER с Replaces для перевода собеседника
				if (heldSessionIdRef.current) {
					const { id, displayName } = heldSessionIdRef.current;
					adapter.referWithReplaces(focusUri, session.id, id, displayName);
					heldSessionIdRef.current = null;

					if (conferenceInviteeRef.current) {
						adapter.referTarget(focusUri, conferenceInviteeRef.current, session.id);
						conferenceInviteeRef.current = null;
					}
				}
			}
		};
		const onCallError = (_sessionId: string, code: number, message: string) => {
			handleError(`Сервер вернул ошибку ${code}: ${message}`);
		};
		const onRedirect = (targetUri: string, isConference = false) => {
			isConferenceRef.current = isConference;

			const constraints = buildConstraints();
			call({ aor: targetUri }, constraints);
		};

		// Hold/unhold
		const onHold = (sessionId: string) => {
			const session = adapter.getSessionById(sessionId);
			if (!session) return;

			if (session.id === sessionId) {
				setCallState({ id: session.id, state: 'hold' });
				updateMedia({ id: session.id, target: 'local', changes: { video: false } });

				disableScreenShare(session.id);
			}
		};
		const onUnHold = (sessionId: string) => {
			const session = adapter.getSessionById(sessionId);
			if (!session) return;

			if (session.id === sessionId) {
				setCallState({ id: session.id, state: 'active' });
				setCurrentCall(session.id);
			}
		};

		// Media
		const onLocalStream = (stream: MediaStream) => {
			setLocalStream(stream);
		};
		const onRemoteStream = (stream: MediaStream) => {
			const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
			setRemoteStream(isFirefox ? new MediaStream(stream) : stream);
		};
		const onRemoteAudioMuted = (sessionId: string, isMute: boolean) => {
			updateMedia({ id: sessionId, target: 'remote', changes: { audio: !isMute } });
		};
		const onRemoteVideoMuted = (sessionId: string, isMute: boolean) => {
			updateMedia({ id: sessionId, target: 'remote', changes: { video: !isMute } });
		};
		const onRemoteHold = (sessionId: string, isHold: boolean) => {
			setCallState({ id: sessionId, state: isHold ? 'remoteHold' : 'active' });

			if (isHold) {
				updateMedia({ id: sessionId, target: 'local', changes: { video: false } });

				disableScreenShare(sessionId);
			}
		};

		const onConferenceInfo = (_target: string, info: ConferenceInfo) => {
			if (!info.entity) return;

			const callId = currentCall?.id;
			if (!callId) return;

			patchCall({
				id: callId,
				changes: { conferenceUsers: info.users },
			});
		};

		const onConferenceSubscriptionTerminated = () => {
			const callId = currentCall?.id;
			if (!callId) return;

			patchCall({
				id: callId,
				changes: { conferenceUsers: [] },
			});
		};

		// Park
		const onParkDialogsUpdated = (dialogs: ParkedDialog[]) => {
			if (!dialogs || dialogs.length === 0) return;

			dialogs.forEach((dialog) => {
				if (dialog.state === 'terminated') {
					removeParkingCall(dialog.id);
				} else {
					addParkingCall(dialog);
				}
			});
		};
		const onParkProgress = ({ stage, code, reason }: ParkProgressEvent) => {
			if (stage !== 'succeeded') return;

			const parts = ['Вызов успешно запаркован'];
			if (code) parts.push(`(${code})`);
			if (reason) parts.push(String(reason));

			pushNotification({
				message: parts.join(' '),
				type: 'info',
				duration: 3000,
				hasRemove: true,
			});
		};
		const onParkFailed = ({ code, reason }: ParkFailureEvent) => {
			const parts = ['Произошла ошибка во время парковки звонка. Повторите попытку позже.'];
			if (code) parts.push(`(${code})`);
			if (reason) parts.push(String(reason));

			handleError(parts);
		};

		// Переадресация
		const onInitRefer = ({ aor, displayName }: SipTarget) => {
			const constraints = buildConstraints();
			call({ aor, displayName }, constraints);
		};

		const onReferNotify = ({ code, referTo }: { code: number; referTo: SipTarget }) => {
			const UNAVAILABLE_CODES = [480, 408, 404, 500];
			const BUSY_CODES = [480, 408, 500];
			const REFER_ERROR_CODES = new Set([...UNAVAILABLE_CODES, ...BUSY_CODES]);
			const referErrorMessages: Record<number, string> = {
				603: 'Приглашение отклонено:', // Абонент отклонил приглашение в конференцию.
				486: 'Приглашение отклонено:', // Абонент сейчас занят и не может присоединиться.
				404: 'не найден', // Абонент не найден.
				480: 'не отвечает', // Абонент временно недоступен.
				408: 'не отвечает', // Время ожидания ответа на приглашение истекло.
				500: 'не отвечает', // Абонент временно недоступен.
			};

			if (!code || !REFER_ERROR_CODES.has(code)) return;

			const { aor, displayName } = referTo;
			const base = referErrorMessages[code];
			const messageParts = [base];

			if (UNAVAILABLE_CODES.includes(code)) messageParts.unshift(displayName || aor);
			else messageParts.push(displayName || aor);
			// if (reason) messageParts.push(reason);

			pushNotification({
				message: messageParts.join(' '),
				type: 'info',
				duration: 3000,
				hasRemove: true,
			});
		};

		const onInitParkCall = (sesId: string) => {
			const constraints = buildConstraints();
			queueMicrotask(() => {
				answer(sesId, constraints);
			});
		};

		// Ended/Failed
		const rejectHandler =
			(state: CallState) =>
			({ session }: SessionInfo) => {
				const date = {
					startedAt: session.start_time?.toISOString() || new Date().toISOString(),
					endedAt: session.end_time?.toISOString() || null,
				};

				patchCall({ id: session.id, changes: { state, ...date } });

				if (session.id === currentCall?.id) {
					setCurrentCall(null);
					setLocalStream(null);
					setRemoteStream(null);

					stopScreenStream();

					unwatchConference();
				}
			};
		const onEnded = rejectHandler('accepted');
		const onFailed = rejectHandler('declined');

		adapter.on('registered', onRegistered);
		adapter.on('unregistered', onUnregistered);
		adapter.on('registrationFailed', onRegistrationFailed);
		adapter.on('sessionCreated', onSessionCreated);
		adapter.on('accepted', onAccepted);
		adapter.on('callError', onCallError);
		adapter.on('redirect', onRedirect);
		adapter.on('hold', onHold);
		adapter.on('unhold', onUnHold);
		adapter.on('localStream', onLocalStream);
		adapter.on('remoteStream', onRemoteStream);
		adapter.on('remoteAudioMuted', onRemoteAudioMuted);
		adapter.on('remoteVideoMuted', onRemoteVideoMuted);
		adapter.on('remoteHold', onRemoteHold);
		adapter.on('conferenceInfoUpdated', onConferenceInfo);
		adapter.on('conferenceSubscriptionTerminated', onConferenceSubscriptionTerminated);
		adapter.on('parkDialogsUpdated', onParkDialogsUpdated);
		adapter.on('parkProgress', onParkProgress);
		adapter.on('parkFailed', onParkFailed);
		adapter.on('ended', onEnded);
		adapter.on('failed', onFailed);
		adapter.on('initReferCall', onInitRefer);
		adapter.on('referNotify', onReferNotify);
		adapter.on('initParkCall', onInitParkCall);

		// cleanup: снимаем все подписки и интервал для конкретного adapter
		return () => {
			adapter.off('registered', onRegistered);
			adapter.off('unregistered', onUnregistered);
			adapter.off('registrationFailed', onRegistrationFailed);
			adapter.off('sessionCreated', onSessionCreated);
			adapter.off('accepted', onAccepted);
			adapter.off('callError', onCallError);
			adapter.off('redirect', onRedirect);
			adapter.off('hold', onHold);
			adapter.off('unhold', onUnHold);
			adapter.off('localStream', onLocalStream);
			adapter.off('remoteStream', onRemoteStream);
			adapter.off('remoteAudioMuted', onRemoteAudioMuted);
			adapter.off('remoteVideoMuted', onRemoteVideoMuted);
			adapter.off('remoteHold', onRemoteHold);
			adapter.off('conferenceInfoUpdated', onConferenceInfo);
			adapter.off('conferenceSubscriptionTerminated', onConferenceSubscriptionTerminated);
			adapter.off('parkDialogsUpdated', onParkDialogsUpdated);
			adapter.off('parkProgress', onParkProgress);
			adapter.off('parkFailed', onParkFailed);
			adapter.off('ended', onEnded);
			adapter.off('failed', onFailed);
			adapter.off('initReferCall', onInitRefer);
			adapter.off('referNotify', onReferNotify);
			adapter.off('initParkCall', onInitParkCall);
		};
	}, [
		autoStart,
		authUser?.name,
		authUser?.sipAor,
		currentCall,
		selectedAudioInput,
		selectedVideoInput,
		disableScreenShare,
		stopScreenStream,
	]);

	useEffect(() => {
		const adapter = adapterRef.current;
		if (!adapter) return;

		if (!currentCall) return;

		const iv = setInterval(async () => {
			try {
				const q = await adapter.getSignalQuality(currentCall.id);
				setSignalQuality(q); // 'good' | 'medium' | 'poor'

				const canBeOffer = adapter.isReadyToReOffer(currentCall.id);
				setIsLoadingRequest(!canBeOffer);
			} catch (error) {
				console.warn(error);
			}
		}, 250);
		return () => clearInterval(iv);
	}, [currentCall]);

	useEffect(() => {
		switchAudioInput(selectedAudioInput);
	}, [selectedAudioInput]);

	useEffect(() => {
		switchVideoInput(selectedVideoInput);
	}, [selectedVideoInput]);

	return {
		isRegistered,
		isLoadingRequest,
		signalQuality,
		buildConstraints,
		register,
		unregister,
		call,
		answer,
		reject,
		hangup,
		hold,
		resumeById,
		setAudioEnabled,
		setVideoEnabled,
		shareScreen,
		getSelfUri,
		getCallIdBySessionId,
		transferCall,
		parkingCall,
		createConference,
		resumeParkedDialog,
		localStream,
		remoteStream,
	};
}
